import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Anchor, Compass, Shield, Mic, Send, BarChart3, ChevronRight, MessageSquare } from "lucide-react";

const FEATURES = [
  { icon: MessageSquare, title: "AI Surveyor Simulation", desc: "Practice with an AI that behaves exactly like a real MMD Surveyor — asks questions, cross-examines, and probes deeper." },
  { icon: Compass, title: "All 3 Functions Covered", desc: "Navigation, Cargo Handling & Stowage, and Ship Operations & Safety — complete preparation for your 2nd Mate orals." },
  { icon: BarChart3, title: "Instant Scoring & Feedback", desc: "Get scored out of 10 after every session with detailed feedback on your strong and weak areas." },
];

const STEPS = [
  { num: "01", title: "Pick a Function", desc: "Choose Navigation, Cargo, or Safety to focus your practice session." },
  { num: "02", title: "AI Asks Questions", desc: "The AI Surveyor asks you 10 oral questions, one at a time, with follow-ups." },
  { num: "03", title: "Speak or Type", desc: "Use your microphone to speak your answer or type it out — just like the real exam." },
  { num: "04", title: "Get Scored", desc: "Receive your score, detailed feedback, and study recommendations instantly." },
];

const PLANS = [
  { name: "Weekly Pass", price: "199", period: "/week", features: ["5 sessions per day", "All 3 functions", "Voice input", "Detailed feedback"], popular: false },
  { name: "Monthly Plan", price: "399", period: "/month", features: ["5 sessions per day", "All 3 functions", "Voice input", "Detailed feedback", "Session history"], popular: true },
  { name: "All Access", price: "599", period: "/month", features: ["5 sessions per day", "All 3 functions", "Voice input", "Priority support", "Session history", "Best value"], popular: false },
];

export default function LandingPage() {
  const { user } = useAuth();
  const ctaLink = user ? "/dashboard" : "/signup";

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="cop-glass fixed top-0 left-0 right-0 z-50 border-b" data-testid="landing-nav">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <Anchor className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg text-[#0a1628]" style={{ fontFamily: 'Manrope' }}>CalmOceanPrep</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Button asChild size="sm"><Link to="/dashboard">Dashboard</Link></Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild><Link to="/login">Log In</Link></Button>
                <Button size="sm" asChild data-testid="nav-signup-btn"><Link to="/signup">Start Free</Link></Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-14 overflow-hidden" data-testid="landing-hero">
        <div className="bg-[#0a1628] text-white">
          <div className="container mx-auto px-4 md:px-6 max-w-5xl py-16 md:py-24">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-sm text-teal-300 mb-6 cop-fade-in">
                <Shield className="h-3.5 w-3.5" />
                <span>Trusted by 2nd Mate candidates across India</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-5 cop-fade-in cop-stagger-1" style={{ fontFamily: 'Manrope' }}>
                Practice Your MMD 2nd Mate Orals with an{" "}
                <span className="text-teal-400">AI Surveyor</span>
              </h1>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8 cop-fade-in cop-stagger-2 max-w-xl">
                The only app where you speak your answers — just like the real exam. Get scored, get feedback, get confident.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 cop-fade-in cop-stagger-3">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white h-12 px-8 text-base" asChild data-testid="hero-cta-btn">
                  <Link to={ctaLink}>Start Free Trial <ChevronRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                <Button size="lg" variant="outline" className="border-slate-500 text-slate-200 hover:bg-white/10 h-12 px-8 text-base" asChild>
                  <a href="#how-it-works">See How It Works</a>
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-4 cop-fade-in cop-stagger-4">2 free sessions. No credit card required.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24" data-testid="landing-features">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-center mb-3" style={{ fontFamily: 'Manrope' }}>
            Why CalmOceanPrep?
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-md mx-auto">
            Built specifically for Indian Merchant Navy candidates preparing for their MMD oral examination.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <Card key={i} className="border bg-white hover:shadow-md transition-shadow" data-testid={`feature-card-${i}`}>
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center mb-4">
                    <f.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-slate-50" data-testid="landing-how-it-works">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-center mb-12" style={{ fontFamily: 'Manrope' }}>
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="text-center cop-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-4xl font-bold text-teal-500/20 mb-3" style={{ fontFamily: 'Manrope' }}>{s.num}</div>
                <h3 className="font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-24" id="pricing" data-testid="landing-pricing">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-center mb-3" style={{ fontFamily: 'Manrope' }}>
            Simple, Transparent Pricing
          </h2>
          <p className="text-muted-foreground text-center mb-12">Start with 2 free sessions. Upgrade when you're ready.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {PLANS.map((p, i) => (
              <Card key={i} className={`border relative ${p.popular ? 'border-primary shadow-lg ring-1 ring-primary' : ''}`} data-testid={`pricing-card-${i}`}>
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs px-3 py-1 rounded-full font-medium">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-1">{p.name}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>{p.price}</span>
                    <span className="text-muted-foreground text-sm">{p.period}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {p.features.map((f, fi) => (
                      <li key={fi} className="text-sm flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full" variant={p.popular ? "default" : "outline"} asChild>
                    <Link to={ctaLink}>Get Started</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a1628] text-white py-10" data-testid="landing-footer">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Anchor className="h-5 w-5 text-teal-400" />
              <span className="font-bold" style={{ fontFamily: 'Manrope' }}>CalmOceanPrep</span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/refund" className="hover:text-white transition-colors">Refund Policy</Link>
              <a href="https://wa.me/7383870103" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                WhatsApp Support
              </a>
            </div>
          </div>
          <p className="text-xs text-slate-500 text-center mt-6">Your AI-Powered MMD Oral Exam Companion</p>
        </div>
      </footer>
    </div>
  );
}
